import React from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing, buildPageSpacing } from '../../styles';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import SafeArea from '../SafeArea';
import { AppBar } from '../OnboardingView/Components/AppBar';
import NotificationHeader from './NotificationHeader';
import NotificationToggleSection from './NotificationToggleSection';
import NotificationListItem from './NotificationListItem';

const NotificationCustomizePage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { preferences, handleToggle, handleToggleAll, areAllEnabled, error, clearError } = useNotificationPreferences();

  const handleBack = () => {
    history.goBack();
  };

  return (
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--background-color);
        display: flex;
        flex-direction: column;
        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
      `}
    >
      {/* Fixed AppBar at the top */}
      <div
        css={css`
          flex-shrink: 0;
          background-color: var(--background-color);
          z-index: 10;
          html[data-theme='dark'] & {
            background-color: ${colors.liquorice};
          }
        `}
      >
        <SafeArea top={true} bottom={false} left={false} right={false}>
          <AppBar
            onBack={handleBack}
            showBackButton={true}
            showTitle={true}
            title={t('notifications.customizeTitle')}
          />
        </SafeArea>
      </div>

      {/* Scrollable content */}
      <div
        css={css`
          flex: 1;
          overflow-y: auto;
          position: relative;
        `}
      >
        <div
          css={css`
            max-width: 1024px;
            margin: 0 auto;
            padding: ${spacing.regular};
            padding-bottom: ${buildPageSpacing(spacing.giga, 'BOTTOM')};
          `}
        >
          <NotificationHeader />

          {/* Error message */}
          {error && (
            <div
              css={css`
                padding: ${spacing.deci};
                background-color: #fee;
                border: 1px solid #fcc;
                border-radius: 8px;
                margin-bottom: ${spacing.hecto};
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <p
                css={css`
                  ${fontStyles.cat};
                  color: #c33;
                  margin: 0;
                `}
              >
                {error}
              </p>
              <button
                css={css`
                  background: none;
                  border: none;
                  color: #c33;
                  cursor: pointer;
                  padding: ${spacing.micro};
                  border-radius: 4px;

                  &:hover {
                    background-color: #fdd;
                  }
                `}
                onClick={clearError}
              >
                ✕
              </button>
            </div>
          )}

          <NotificationToggleSection areAllEnabled={areAllEnabled} onToggleAll={() => void handleToggleAll()} />

          {/* Title for individual notifications */}
          <h2
            css={css`
              ${fontStyles.horse};
              color: var(--text-color);
              margin: 0 0 ${spacing.deci} 0;
              font-weight: 500;
            `}
          >
            {t('notifications.individualNotifications')}
          </h2>

          {/* List of individual notifications */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.centi};
            `}
          >
            {preferences.map((pref) => (
              <NotificationListItem key={pref.id} preference={pref} onToggle={handleToggle} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCustomizePage;
